import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import Spinner from "./components/Spinner";
import { SessionProvider } from "./lib/session";
import { VolunteerSessionProvider } from "./lib/volunteerSession";
import { AuthedParishProvider, PublicParishProvider } from "./lib/parishContext";
import { supabase, isSupabaseEnabled } from "./lib/supabaseClient";
import { resolveVolunteerParishSlug } from "./lib/roleCheck";
import ParishTheme from "./components/ParishTheme";
import SacristyTheme from "./components/SacristyTheme";
import Landing from "./views/Landing";
import Signup from "./views/Signup";
import ProductHelp from "./views/ProductHelp";
import ParishHelp from "./views/ParishHelp";
import PublicSchedule from "./views/PublicSchedule";
import SignIn from "./views/SignIn";
import VolunteerSignup from "./views/VolunteerSignup";
import VolunteerLogin from "./views/VolunteerLogin";
import VolunteerResetPassword from "./views/VolunteerResetPassword";
import ResetPassword from "./views/ResetPassword";
import VolunteerDashboard from "./views/VolunteerDashboard";
import VolunteerWelcome from "./views/VolunteerWelcome";
import PublicParishSchedule from "./views/PublicParishSchedule";
import "./styles/global.css";

// Routes visible to signed-out users when Supabase auth is configured:
// the quiet landing page at `/` and the sign-in form at `/sign-in`.
// Anything else routes back to `/` so deep-link guesses don't dump a
// stranger straight into the credentials form.
// (Public parish surfaces — /:parish/help and /:parish/schedule/:token
// — live above SessionProvider at the top-level Routes; they never
// reach this fallback.)
function SignedOutRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function BootSplash() {
  return (
    <main className="page page--boot-splash">
      <Spinner label="Loading Sacristy" />
    </main>
  );
}

// The parish help center is fully public and parish-scoped. It must
// render for signed-out visitors without redirecting to /sign-in, so
// it lives above SessionProvider and bypasses the auth tree entirely.
// ParishTheme tags the document with the parish branding (route-
// driven, never tied to session — a signed-in scheduler hitting this
// URL also sees the parish view, as a volunteer would).
function ParishHelpRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <ParishTheme>
        <ParishHelp />
      </ParishTheme>
    </PublicParishProvider>
  );
}

// Same posture as the help center: public, parish-themed,
// token-gated personal schedule for a single volunteer. Token
// lives in the URL; worker validates it on /api/schedule/:slug/:token.
function PublicScheduleRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <ParishTheme>
        <PublicSchedule />
      </ParishTheme>
    </PublicParishProvider>
  );
}

// Volunteer portal — self-serve signup, login, and the volunteer's own
// dashboard. Parish-scoped like the help center and public schedule
// (a link Colleen shares carries the parish slug), but wears
// Sacristy AI's own product chrome (SacristyTheme) rather than the
// parish's — these are app screens, not a public marketing page, and
// the sprint's design directive is "feel like the same product, just
// a different role". PublicParishProvider still loads the parish row
// so the header's ParishChip and the signup form's parish_id linkage
// work without a session (there isn't one yet on signup/login).
function VolunteerSignupRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <SacristyTheme>
        <VolunteerSignup />
      </SacristyTheme>
    </PublicParishProvider>
  );
}

// Parish-branded like the help center — this is the parish welcoming
// its own volunteers, not a Sacristy product screen.
function VolunteerWelcomeRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <ParishTheme>
        <VolunteerWelcome />
      </ParishTheme>
    </PublicParishProvider>
  );
}

function VolunteerLoginRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <SacristyTheme>
        <VolunteerLogin />
      </SacristyTheme>
    </PublicParishProvider>
  );
}

// Parish-scoped admin entry point, per the RBAC sprint's "admin login
// moves to a distinct route" requirement. Reuses the SignIn component
// as-is rather than forking it — no SessionProvider ancestor here (this
// route sits above the scheduler auth tree, same posture as signup/
// login), so SignIn's "already signed in" shortcut just doesn't fire
// in this context; the form itself works identically either way.
// /sign-in keeps working too (existing bookmarks, Landing's staff
// link) — this doesn't replace it, it's the new canonical entry.
function AdminLoginRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <SacristyTheme>
        <SignIn />
      </SacristyTheme>
    </PublicParishProvider>
  );
}

// Where the "Reset password" email link lands. No VolunteerSessionProvider
// here — this page manages its own narrow recovery-session detection
// directly (see VolunteerResetPassword.tsx) rather than going through
// the volunteer_accounts-row hydration the dashboard needs.
function VolunteerResetPasswordRoute() {
  const { parish } = useParams();
  return (
    <PublicParishProvider slug={parish ?? ""}>
      <SacristyTheme>
        <VolunteerResetPassword />
      </SacristyTheme>
    </PublicParishProvider>
  );
}

// The volunteer's own dashboard needs a real session, so it gets its
// own VolunteerSessionProvider (separate from the scheduler
// SessionProvider in AuthedShell below — see lib/volunteerSession.tsx
// for why these are kept apart). Signed-out visitors bounce to
// /:parish/login; a signed-in identity with no volunteer_accounts row
// (e.g. a scheduler's own login landing here) gets an explanatory
// screen instead of a silent 404.
function VolunteerDashboardRoute() {
  const { parish } = useParams();
  const slug = parish ?? "";
  return (
    <PublicParishProvider slug={slug}>
      <SacristyTheme>
        <VolunteerSessionProvider
          fallback={<BootSplash />}
          signedOutFallback={<Navigate to={`/${slug}/login`} replace />}
          notVolunteerFallback={<VolunteerAccountMismatch slug={slug} />}
        >
          <VolunteerDashboard />
        </VolunteerSessionProvider>
      </SacristyTheme>
    </PublicParishProvider>
  );
}

function VolunteerAccountMismatch({ slug }: { slug: string }) {
  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <h1 className="sign-in__title">Sacristy AI</h1>
        <p className="sign-in__lede">
          This sign-in isn't set up as a volunteer account. If you're a parish scheduler, sign in{" "}
          <a href={`/${slug}/admin/login`}>here</a> instead.
        </p>
        <div className="sign-in__alt">
          <a className="sign-in__link" href={`/${slug}/login`}>
            Back to volunteer sign-in
          </a>
        </div>
      </div>
    </main>
  );
}

// Legacy /schedule/:parish/:token URLs (issued before the route
// shape moved to /:parish/schedule/:token) redirect in place so
// emails already in inboxes don't break.
function LegacyScheduleRedirect() {
  const { parish, token } = useParams();
  if (!parish || !token) return <Navigate to="/sign-in" replace />;
  return <Navigate to={`/${parish}/schedule/${token}`} replace />;
}

// Frontend route guard (defense-in-depth — the worker's role gate in
// worker/src/lib/roles.ts is the actual boundary, this only decides
// what to render). Checks whether the current session — however it
// was established: password, magic link, a stale tab, anything —
// belongs to a volunteer, and if so what parish. Runs once per
// AuthedShell mount; the onAuthStateChange subscription keeps it
// current if someone signs in/out without a full page reload.
type VolunteerCheck =
  | { kind: "checking" }
  | { kind: "not-volunteer" }
  | { kind: "volunteer"; parishSlug: string };

function useVolunteerRedirectCheck(): VolunteerCheck {
  const [state, setState] = useState<VolunteerCheck>(
    isSupabaseEnabled ? { kind: "checking" } : { kind: "not-volunteer" },
  );

  useEffect(() => {
    if (!isSupabaseEnabled || !supabase) return;
    let cancelled = false;

    async function check(userId: string | undefined) {
      if (!userId) {
        if (!cancelled) setState({ kind: "not-volunteer" });
        return;
      }
      const slug = await resolveVolunteerParishSlug(userId);
      if (cancelled) return;
      setState(slug ? { kind: "volunteer", parishSlug: slug } : { kind: "not-volunteer" });
    }

    supabase.auth.getSession().then(({ data }) => check(data.session?.user.id));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void check(session?.user.id);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

// Authenticated scheduler shell. Wears Sacristy AI's own brand
// (parchment + gold + Playfair) regardless of which parish the
// user belongs to — that's the SacristyTheme boundary. The
// AuthedParishProvider still loads parish data so the dashboard
// header can show a small "you are in: <parish>" chip and the
// settings page can edit the parish row.
//
// A volunteer session hitting ANY admin-app URL (root, /settings,
// /holy-week, whatever) redirects to their own dashboard instead of
// ever reaching SessionProvider/App — this is the "volunteer can't
// reach the admin dashboard by any route" + "root lands volunteers on
// their dashboard" requirements in one guard, since every unscoped
// admin URL funnels through this same catch-all.
function AuthedShell() {
  const volunteerCheck = useVolunteerRedirectCheck();

  if (volunteerCheck.kind === "checking") return <BootSplash />;
  if (volunteerCheck.kind === "volunteer") {
    return <Navigate to={`/${volunteerCheck.parishSlug}/dashboard`} replace />;
  }

  return (
    <SessionProvider fallback={<BootSplash />} signedOutFallback={<SignedOutRoutes />}>
      <AuthedParishProvider>
        <SacristyTheme>
          <App />
        </SacristyTheme>
      </AuthedParishProvider>
    </SessionProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public capability-URL schedule page. Fully unauthenticated
              and outside every provider — it renders only what the
              public endpoint returns. Kept at a short top-level /s/
              path so the shared link is easy to type off a bulletin. */}
          <Route path="/s/:slug" element={<PublicParishSchedule />} />
          <Route path="/:parish/help" element={<ParishHelpRoute />} />
          <Route path="/:parish/schedule/:token" element={<PublicScheduleRoute />} />
          {/* Volunteer front door — where invite emails point.
              Parish-branded (ParishTheme, like the help center), no
              product marketing. The coordinator-facing marketing
              page at "/" is the wrong landing for a volunteer. */}
          <Route path="/:parish/welcome" element={<VolunteerWelcomeRoute />} />
          <Route path="/:parish/signup" element={<VolunteerSignupRoute />} />
          <Route path="/:parish/login" element={<VolunteerLoginRoute />} />
          <Route path="/:parish/admin/login" element={<AdminLoginRoute />} />
          <Route path="/:parish/reset-password" element={<VolunteerResetPasswordRoute />} />
          {/* Where the STAFF reset-password email link lands (sent by
              SignIn's forgot-password form). Above SessionProvider
              like the volunteer variant — the page manages its own
              narrow recovery-session detection. */}
          <Route
            path="/reset-password"
            element={
              <SacristyTheme>
                <ResetPassword />
              </SacristyTheme>
            }
          />
          <Route path="/:parish/dashboard" element={<VolunteerDashboardRoute />} />
          {/* Coordinator self-serve signup (Phase 0 onboarding). One
              route — wizard steps are component state persisted to
              localStorage, not sub-paths, because /:parish/* owns
              every two-segment URL and step paths would read as
              parish slugs. Above SessionProvider like the other
              pre-session screens; wears product chrome. */}
          <Route
            path="/signup"
            element={
              <SacristyTheme>
                <Signup />
              </SacristyTheme>
            }
          />
          {/* Product help pages — public and parish-agnostic (the
              parish help center at /:parish/help is a separate
              surface). Content compiled from content/help/*.md. */}
          <Route
            path="/help/:page"
            element={
              <SacristyTheme>
                <ProductHelp />
              </SacristyTheme>
            }
          />
          <Route
            path="/schedule/:parish/:token"
            element={<LegacyScheduleRedirect />}
          />
          <Route path="*" element={<AuthedShell />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
